import type { CoachingReport, Exercise, KeyMoment, ParsedGame } from '../../types';
import { hashString } from '../pgn/parse';

const MAX_EXERCISES = 3;
const DEFAULT_TOLERANCE_CP = 40; // un peu moins d'un demi-pion : assez strict pour rester pédagogique

/**
 * Construit jusqu'à 3 exercices à partir des positions pertinentes déjà
 * confirmées par l'analyse (CoachingReport.momentsForExercises). Ne fabrique
 * jamais d'exercice arbitraire : si la partie ne fournit pas assez de
 * positions pertinentes, moins d'exercices sont proposés.
 */
export function buildExercisesFromReport(game: ParsedGame, report: CoachingReport): Exercise[] {
  const sources = report.momentsForExercises.slice(0, MAX_EXERCISES);
  return sources.map((moment) => momentToExercise(game, moment));
}

function momentToExercise(game: ParsedGame, moment: KeyMoment): Exercise {
  const hints = buildProgressiveHints(moment);
  return {
    id: hashString(`${game.id}:${moment.id}:exercise`),
    gameId: game.id,
    momentId: moment.id,
    fen: moment.fenBefore,
    playerColor: moment.playerColor,
    goalUci: moment.bestUci,
    goalSan: moment.bestSan,
    referenceEvalCp: moment.evalAfterBestCp,
    toleranceCp: DEFAULT_TOLERANCE_CP,
    hints,
    solutionSan: moment.bestSan,
    solutionLineSan: moment.bestLineSan,
    theme: moment.themes[0] ?? null,
    createdAt: new Date().toISOString(),
    review: { dueAt: new Date().toISOString(), intervalDays: 0, lastOutcome: null, streak: 0 },
    // Présent seulement si l'analyse a pu calculer une punition exploitable
    // (cf. computeReplyFacts) : alimente l'étape « qu'est-ce que ça menaçait ? »
    // avant de chercher le meilleur coup (cf. ExercisePlayer).
    mistake: moment.punishment
      ? { playedSan: moment.playedSan, playedUci: moment.playedUci, punishment: moment.punishment }
      : undefined,
  };
}

function buildProgressiveHints(moment: KeyMoment): string[] {
  const pieceHint = describePieceHint(moment.bestUci);
  const hints = [
    `Indice 1 : concentre-toi sur la zone autour de la case ${moment.bestUci.slice(0, 2)}.`,
    `Indice 2 : ${pieceHint}`,
    `Indice 3 : le coup commence en ${moment.bestUci.slice(0, 2)} et va vers ${moment.bestUci.slice(2, 4)}.`,
  ];
  return hints;
}

function describePieceHint(uci: string): string {
  const from = uci.slice(0, 2);
  return `Une pièce en ${from} a un coup nettement meilleur que ce qui a été joué.`;
}
